/**
 * Dashboard Page
 *
 * Main overview with real performance metrics, recent activity, and quick insights.
 * Features refined stat cards with Lucide icons and polished styling.
 */

import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Users,
  ClipboardCheck,
  BarChart3,
  FileText,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  ArrowRight,
  Activity,
  AlertTriangle,
  MessageSquareMore,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  useDashboardKpis,
  useDashboardRecent,
  useDashboardAttention,
  useOperatorFeedbackSummary,
} from '../../hooks/useDashboard';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useDashboardTrends } from '../../hooks/useDashboard';
import { getScoreColor } from '../../lib/score-colors';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { Ring2ShiftWidget } from '../../components/dashboard/Ring2ShiftWidget';
import { useRoleLevel } from '../../lib/hooks/useRoleLevel';

export function DashboardPage() {
  useDocumentTitle('Dashboard');
  // useAuth0 still used here for `user.name` (greeting) — display only.
  const { user } = useAuth0();

  // Phase 25.5: Gate Ring 2 widget to manager+ (level 3+). Operators get
  // their own portal and never see this dashboard page, but we guard here
  // defensively anyway.
  // Phase 19.S frontend migration (May 6, 2026) — role now flows from
  // DB-canonical /api/me via useRoleLevel, not the JWT claim.
  const { level: roleLevel } = useRoleLevel();
  const canSeeRing2Widget = roleLevel >= 3;

  // Fetch real data
  const { data: kpis, isLoading: kpisLoading, dataUpdatedAt } = useDashboardKpis();
  const { data: trends } = useDashboardTrends(30);
  const { data: recentAssessments } = useDashboardRecent(5);
  const { data: attentionOperators } = useDashboardAttention();
  const { data: feedbackSummary } = useOperatorFeedbackSummary();

  // Phase 41A (G-20): Recent Assessments and Needs Attention name operators, so
  // they render only for an org with an assessment in the last 30 days. Hidden
  // while the KPIs load, and against an older API that sends no last30Days.
  const showOperatorWidgets = (kpis?.assessments.last30Days ?? 0) > 0;

  const firstName = user?.name?.split(' ')[0] || 'there';
  const greeting = getGreeting();

  // Format chart data — filter out days with no assessments so the line only
  // connects real data points instead of dropping to 0 on empty days
  const chartData = (trends ?? [])
    .filter((d) => d.count > 0)
    .map((d) => ({
      date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      score: d.avgScore ?? 0,
      count: d.count,
    }));
  const hasChartData = chartData.length > 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-sm font-medium text-guild-green mb-1">
            {greeting}
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Welcome back, {firstName}
          </h1>
          <p className="mt-2 text-gray-500 max-w-lg">
            Here's an overview of your operator performance metrics and recent activity.
          </p>
        </div>
        <div className="hidden lg:flex flex-col items-end gap-1">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Clock size={16} />
            <span>Auto-refreshes every minute</span>
          </div>
          {dataUpdatedAt > 0 && (
            <span className="text-xs text-gray-300">
              Last updated {new Date(dataUpdatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Operators"
          value={kpisLoading ? '--' : String(kpis?.operators.value ?? 0)}
          change={null}
          trend="neutral"
          icon={Users}
          accentColor="guild-green"
        />
        <StatCard
          title="Total Assessments"
          value={kpisLoading ? '--' : String(kpis?.assessments.value ?? 0)}
          change={kpis?.assessments.change ? `${kpis.assessments.change > 0 ? '+' : ''}${kpis.assessments.change.toFixed(0)}%` : null}
          trend={kpis?.assessments.change ? (kpis.assessments.change >= 0 ? 'up' : 'down') : 'neutral'}
          icon={ClipboardCheck}
          accentColor="blue"
          subtitle={kpis?.assessments.thisWeek ? `${kpis.assessments.thisWeek} this week` : undefined}
        />
        <StatCard
          title="Average Score"
          value={kpisLoading ? '--' : (kpis?.averageScore?.value != null ? kpis.averageScore.value.toFixed(0) : '—')}
          change={kpis?.averageScore?.change != null ? `${kpis.averageScore.change > 0 ? '+' : ''}${kpis.averageScore.change.toFixed(1)}` : null}
          trend={kpis?.averageScore?.change ? (kpis.averageScore.change >= 0 ? 'up' : 'down') : 'neutral'}
          icon={BarChart3}
          accentColor="emerald"
        />
        <StatCard
          title="Active SOPs"
          value={kpisLoading ? '--' : String(kpis?.sops.value ?? 0)}
          change={null}
          trend="neutral"
          icon={FileText}
          accentColor="violet"
        />
      </div>

      {/* Phase 25.5: Manager Ring 2 "This week on your shifts" widget */}
      {canSeeRing2Widget && <Ring2ShiftWidget />}

      {/* Trend Chart */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-guild-mint/50">
              <Activity size={18} className="text-guild-green" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">
              Score Trend (30 Days)
            </h2>
          </div>
        </div>
        <div className="p-6">
          {hasChartData ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00674a" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#00674a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                    formatter={(value) => [Number(value).toFixed(1), 'Avg Score']}
                  />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke="#00674a"
                    strokeWidth={2}
                    fill="url(#scoreGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState
              icon={Activity}
              title="No score data yet"
              description="Assessment scores will be charted here once evaluations are completed."
            />
          )}
        </div>
      </div>

      {/* Content Grid */}
      {showOperatorWidgets && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Assessments */}
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50">
                  <ClipboardCheck size={18} className="text-blue-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                  Recent Assessments
                </h2>
              </div>
              <Link
                to="/assessments"
                className="group flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-guild-green transition-colors"
              >
                View all
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
            <div className="p-4">
              {!recentAssessments || recentAssessments.length === 0 ? (
                <EmptyState
                  icon={Activity}
                  title="No assessments yet"
                  description="Assessment data will appear here once operators begin their evaluations."
                />
              ) : (
                <div className="space-y-2">
                  {recentAssessments.map((assessment) => (
                    <Link
                      key={assessment.id}
                      to={`/assessments/${assessment.id}`}
                      className="flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold ${getScoreColor(assessment.overallScore, 'badge')}`}>
                          {assessment.overallScore.toFixed(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {assessment.operator.firstName} {assessment.operator.lastName}
                          </p>
                          <p className="text-xs text-gray-500 line-clamp-1">{assessment.sop.title}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                          assessment.assessmentType === 'auto'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-purple-50 text-purple-700'
                        }`}>
                          {assessment.assessmentType === 'auto' ? 'Auto' : 'Manual'}
                        </span>
                        <p className="mt-1 text-xs text-gray-400">
                          {formatRelativeTime(assessment.assessedAt)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Operators Needing Attention */}
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50">
                  <AlertTriangle size={18} className="text-amber-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                  Needs Attention
                </h2>
              </div>
              <Link
                to="/skill-matrix"
                className="group flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-guild-green transition-colors"
              >
                View matrix
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
            <div className="p-4">
              {!attentionOperators || attentionOperators.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title="All operators on track"
                  description="No operators currently need attention. Great job!"
                />
              ) : (
                <div className="space-y-2">
                  {attentionOperators.slice(0, 5).map((operator) => (
                    <Link
                      key={operator.id}
                      to={`/operators/${operator.id}`}
                      className="flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-gray-50"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {operator.firstName} {operator.lastName}
                        </p>
                        <p className="text-xs text-gray-500">{operator.employeeId}</p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getReasonStyle(operator.reason)}`}>
                          {getReasonLabel(operator.reason)}
                        </span>
                        {operator.avgScore !== null && (
                          <p className={`mt-1 text-sm font-semibold ${operator.avgScore < 60 ? 'text-red-600' : 'text-amber-600'}`}>
                            {operator.avgScore.toFixed(0)} avg
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Operator Feedback Card */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50">
              <MessageSquareMore size={18} className="text-violet-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">
              Operator Feedback
            </h2>
          </div>
          <Link
            to="/analytics/operator-feedback"
            className="group flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-guild-green transition-colors"
          >
            View all
            <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
        <div className="p-6">
          {!feedbackSummary ||
          (feedbackSummary.assessmentFairness.total === 0 &&
            feedbackSummary.trainingRating.total === 0) ? (
            <EmptyState
              icon={MessageSquareMore}
              title="No feedback yet"
              description="Operator feedback on assessments and training will appear here."
            />
          ) : (
            <div className="space-y-5">
              {/* Rate gauges */}
              <div className="grid grid-cols-2 gap-4">
                <FeedbackGauge
                  label="Assessment Fairness"
                  rate={feedbackSummary.assessmentFairness.rate}
                  positive={feedbackSummary.assessmentFairness.positive}
                  total={feedbackSummary.assessmentFairness.total}
                />
                <FeedbackGauge
                  label="Training Satisfaction"
                  rate={feedbackSummary.trainingRating.rate}
                  positive={feedbackSummary.trainingRating.positive}
                  total={feedbackSummary.trainingRating.total}
                />
              </div>

              {/* Recent feedback list */}
              {feedbackSummary.recent.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                    Recent Feedback
                  </h3>
                  <div className="space-y-2">
                    {feedbackSummary.recent.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 rounded-xl p-3 bg-gray-50"
                      >
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                            item.sentiment === 'positive'
                              ? 'bg-emerald-100 text-emerald-600'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {item.sentiment === 'positive' ? (
                            <ThumbsUp size={14} />
                          ) : (
                            <ThumbsDown size={14} />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {item.operatorName ?? 'Anonymous'}
                            </p>
                            <span
                              className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                                item.feedbackType === 'assessment_fairness'
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-purple-50 text-purple-700'
                              }`}
                            >
                              {item.feedbackType === 'assessment_fairness'
                                ? 'Assessment'
                                : 'Training'}
                            </span>
                          </div>
                          {item.comment && (
                            <p className="mt-0.5 text-xs text-gray-500 line-clamp-1">
                              {item.comment}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            {formatRelativeTime(item.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helpers

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}


function getReasonStyle(reason: string): string {
  switch (reason) {
    case 'low_score':
      return 'bg-red-50 text-red-700';
    case 'stale_assessments':
      return 'bg-amber-50 text-amber-700';
    case 'no_assessments':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function getReasonLabel(reason: string): string {
  switch (reason) {
    case 'low_score':
      return 'Low Score';
    case 'stale_assessments':
      return 'Stale';
    case 'no_assessments':
      return 'No Data';
    default:
      return reason;
  }
}

function formatRelativeTime(date: string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}

// Components

interface StatCardProps {
  title: string;
  value: string;
  change: string | null;
  trend: 'up' | 'down' | 'neutral';
  icon: LucideIcon;
  accentColor: 'guild-green' | 'blue' | 'emerald' | 'violet';
  subtitle?: string;
}

const accentStyles = {
  'guild-green': {
    bg: 'bg-guild-mint/50',
    icon: 'text-guild-green',
  },
  blue: {
    bg: 'bg-blue-50',
    icon: 'text-blue-600',
  },
  emerald: {
    bg: 'bg-emerald-50',
    icon: 'text-emerald-600',
  },
  violet: {
    bg: 'bg-violet-50',
    icon: 'text-violet-600',
  },
};

function StatCard({ title, value, change, trend, icon: Icon, accentColor, subtitle }: StatCardProps) {
  const styles = accentStyles[accentColor];

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-gray-400';

  return (
    <div className="group relative rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-200">
      <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${styles.bg}`}>
        <Icon size={22} strokeWidth={2} className={styles.icon} />
      </div>

      <div className="mt-4">
        <p className="text-3xl font-bold tracking-tight text-gray-900">{value}</p>
        <p className="mt-1 text-sm font-medium text-gray-500">{title}</p>
        {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
      </div>

      {change && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-1.5">
          <TrendIcon size={14} className={trendColor} />
          <span className={`text-xs font-semibold ${trendColor}`}>{change}</span>
          <span className="text-xs text-gray-400">vs last week</span>
        </div>
      )}

      <div className={`absolute bottom-0 left-6 right-6 h-0.5 rounded-full ${styles.bg} opacity-0 group-hover:opacity-100 transition-opacity`} />
    </div>
  );
}

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
}

function EmptyState({ icon: Icon, title, description }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 mb-4">
        <Icon size={24} className="text-gray-300" />
      </div>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-400 max-w-xs">{description}</p>
    </div>
  );
}

interface FeedbackGaugeProps {
  label: string;
  rate: number | null;
  positive: number;
  total: number;
}

function FeedbackGauge({ label, rate, positive, total }: FeedbackGaugeProps) {
  if (total === 0) {
    return (
      <div className="rounded-xl bg-gray-50 p-4 text-center">
        <p className="text-2xl font-bold text-gray-300">&mdash;</p>
        <p className="mt-1 text-xs font-medium text-gray-500">{label}</p>
        <p className="text-xs text-gray-400">No data</p>
      </div>
    );
  }

  const rateColor =
    rate !== null && rate >= 75
      ? 'text-emerald-600'
      : rate !== null && rate >= 50
        ? 'text-amber-600'
        : 'text-red-600';

  return (
    <div className="rounded-xl bg-gray-50 p-4 text-center">
      <p className={`text-2xl font-bold ${rateColor}`}>{rate}%</p>
      <p className="mt-1 text-xs font-medium text-gray-500">{label}</p>
      <p className="text-xs text-gray-400">
        {positive} of {total} positive
      </p>
    </div>
  );
}

export default DashboardPage;
